import { useEffect, useMemo, useState } from "react";
import { getCategoryName } from "../../utils/adminDashboard";
import {
  adminOrderService,
  createCategory,
  createCity,
  createPincode,
  createSubCategory,
  deleteCategory,
  deleteCity,
  deletePincode,
  deleteSubCategory,
  getAdminDashboardData,
  getAdmins,
  getCustomerAddressesAndOrders,
  getOrders,
  getPincodes,
  updateCategory,
  updateCity,
  updatePincode,
  updateSubCategory,
} from "../../services/adminDashboardService";

const getStoredUser = () => JSON.parse(localStorage.getItem("user") || "null");

const getErrorMessage = (error, fallback) => {
  const status = error?.response?.status;
  const detail =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    (typeof error?.response?.data === "string" ? error.response.data : "") ||
    error?.message;

  return [fallback, status ? `(${status})` : "", detail].filter(Boolean).join(" ");
};

export const useAdminDashboard = (navigate) => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [orders, setOrders] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [categories, setCategories] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [cities, setCities] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [categoryForm, setCategoryForm] = useState({ category: "", forReSell: false });
  const [subCategoryForm, setSubCategoryForm] = useState({
    category: "",
    subCategory: "",
    fixedPrice: true,
    price: "",
    minPrice: "",
    maxPrice: "",
  });
  const [cityForm, setCityForm] = useState({ name: "" });
  const [pincodeForm, setPincodeForm] = useState({ pinCode: "", available: true });
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingSubCategoryId, setEditingSubCategoryId] = useState(null);
  const [editingCityId, setEditingCityId] = useState(null);
  const [editingPincodeId, setEditingPincodeId] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [showProfile, setShowProfile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [categorySaving, setCategorySaving] = useState(false);
  const [subCategorySaving, setSubCategorySaving] = useState(false);
  const [pincodeSaving, setPincodeSaving] = useState(false);
  const [error, setError] = useState("");

  const user = getStoredUser();
  const adminName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.username || "Admin";
  const adminInitial = adminName.charAt(0).toUpperCase();

  const selectedCategory = useMemo(() => {
    if (selectedCategoryId) {
      return categories.find((category) => String(category.id) === String(selectedCategoryId));
    }

    return categories[0];
  }, [categories, selectedCategoryId]);

  const findCategoryByFormValue = (value) =>
    categories.find(
      (category) =>
        String(category.id) === String(value) ||
        getCategoryName(category).toLowerCase() === String(value || "").toLowerCase()
    );

  const findParentCategoryForSubCategory = (subCategory) => {
    const directCategory = findCategoryByFormValue(subCategory?.categoryId || subCategory?.categoryID);

    if (directCategory) {
      return directCategory;
    }

    return categories.find((category) =>
      (category.subCategories || []).some((item) => String(item.id) === String(subCategory?.id))
    );
  };

  const requireAdminSession = (resourceName) => {
    const token = localStorage.getItem("token");

    if (!token || !user?.id) {
      window.alert(`Please login before managing ${resourceName}`);
      navigate("/login");
      return false;
    }

    return true;
  };

  const replaceOrder = (updatedOrder) => {
    setOrders((current) =>
      current.map((order) =>
        String(order.id) === String(updatedOrder.id) ? updatedOrder : order
      )
    );
  };

  const fetchAll = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await getAdminDashboardData();
      setOrders(data.orders);
      setCategories(data.categories);
      setAddresses(data.addresses);
      setCities(data.cities);
      setPincodes(data.pincodes || []);
      setAdmins(data.admins);
    } catch (err) {
      setError(err.message || "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setOrders(await getOrders());
    } catch (err) {
      const status = err.response?.status;
      const detail = status ? ` (${status})` : "";
      setError(`Failed to load orders${detail}`);
    }
  };

  const fetchCustomerAddresses = async () => {
    try {
      const data = await getCustomerAddressesAndOrders();
      setAddresses(data.addresses);
      setOrders(data.orders);
    } catch {
      setError("Failed to load customer addresses");
    }
  };

  const fetchAdmins = async () => {
    try {
      setAdmins(await getAdmins());
    } catch {
      setError("Failed to load admins");
    }
  };

  const fetchPincodes = async () => {
    try {
      setPincodes(await getPincodes());
    } catch (err) {
      setPincodes([]);
      window.alert(err.message || "Failed to load pincodes");
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    if (activeTab === "orders") {
      fetchOrders();
    }

    if (activeTab === "addresses") {
      fetchCustomerAddresses();
    }

    if (activeTab === "pincodes") {
      fetchPincodes();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!selectedCategoryId && categories.length > 0) {
      setSelectedCategoryId(String(categories[0].id));
    }
  }, [categories, selectedCategoryId]);

  useEffect(() => {
    if (!selectedState && cities.length > 0) {
      setSelectedState(cities[0]?.name || "");
    }
  }, [cities, selectedState]);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const handleCategoryCancel = () => {
    setEditingCategoryId(null);
    setCategoryForm({ category: "", forReSell: false });
  };

  const handleSubCategoryCancel = () => {
    setEditingSubCategoryId(null);
    setSubCategoryForm({
      category: "",
      subCategory: "",
      fixedPrice: true,
      price: "",
      minPrice: "",
      maxPrice: "",
    });
  };

  const handleCityCancel = () => {
    setEditingCityId(null);
    setCityForm({ name: "" });
  };

  const handlePincodeCancel = () => {
    setEditingPincodeId(null);
    setPincodeForm({ pinCode: "", available: true });
  };

  const handleCategorySubmit = async () => {
    if (!requireAdminSession("categories")) {
      return false;
    }

    if (!categoryForm.category.trim()) {
      window.alert("Please enter category name");
      return false;
    }

    setCategorySaving(true);

    try {
      const payload = {
        category: categoryForm.category.trim(),
        forReSell: Boolean(categoryForm.forReSell),
        createdUserID: Number(user?.id) || 0,
        updatedCategoryID: Number(user?.id) || 0,
      };

      if (editingCategoryId) {
        const updatedCategory = await updateCategory(editingCategoryId, payload);
        setCategories((current) =>
          current.map((category) =>
            String(category.id) === String(editingCategoryId)
              ? { ...updatedCategory, subCategories: category.subCategories || [] }
              : category
          )
        );
      } else {
        const newCategory = await createCategory(payload);
        setCategories((current) => [
          ...current,
          { ...newCategory, subCategories: newCategory.subCategories || [] },
        ]);
        setSelectedCategoryId((current) => current || String(newCategory.id));
      }

      handleCategoryCancel();
      return true;
    } catch (err) {
      window.alert(getErrorMessage(err, "Failed to save category"));
      return false;
    } finally {
      setCategorySaving(false);
    }
  };

  const handleCategoryEdit = (category) => {
    setSelectedCategoryId(String(category.id));
    setEditingCategoryId(category.id);
    setCategoryForm({
      category: getCategoryName(category),
      forReSell: Boolean(category.forReSell),
    });
  };

  const handleCategoryDelete = async (category) => {
    if (!requireAdminSession("categories")) {
      return;
    }

    const categoryName = getCategoryName(category);
    const shouldDelete = window.confirm(`Delete ${categoryName} and its subcategories?`);

    if (!shouldDelete) {
      return;
    }

    try {
      await deleteCategory(category.id);
      setCategories((current) => current.filter((item) => String(item.id) !== String(category.id)));

      if (String(selectedCategoryId) === String(category.id)) {
        const nextCategory = categories.find((item) => String(item.id) !== String(category.id));
        setSelectedCategoryId(nextCategory ? String(nextCategory.id) : "");
      }

      if (String(editingCategoryId) === String(category.id)) {
        handleCategoryCancel();
      }
    } catch (err) {
      window.alert(err.message || "Failed to delete category");
    }
  };

  const handleSubCategorySubmit = async () => {
    if (!requireAdminSession("subcategories")) {
      return false;
    }

    const formCategory = findCategoryByFormValue(subCategoryForm.category);
    const targetCategory = formCategory || selectedCategory;

    if (!targetCategory?.id) {
      window.alert("Please select a parent category");
      return false;
    }

    if (!subCategoryForm.subCategory.trim()) {
      window.alert("Please enter subcategory name");
      return false;
    }

    const isFixedPrice = Boolean(subCategoryForm.fixedPrice);
    const singlePrice = Number(subCategoryForm.price);
    const minPrice = Number(subCategoryForm.minPrice);
    const maxPrice = Number(subCategoryForm.maxPrice);
    const hasSinglePrice = Number.isFinite(singlePrice) && singlePrice > 0;
    const hasMinPrice = Number.isFinite(minPrice) && minPrice > 0;
    const hasMaxPrice = Number.isFinite(maxPrice) && maxPrice > 0;

    if (isFixedPrice && !hasSinglePrice) {
      window.alert("Please enter a fixed price");
      return false;
    }

    if (!isFixedPrice && (!hasMinPrice || !hasMaxPrice)) {
      window.alert("Please enter both min and max price");
      return false;
    }

    if (!isFixedPrice && maxPrice < minPrice) {
      window.alert("Max price cannot be less than min price");
      return false;
    }

    const normalizedMinPrice = isFixedPrice ? singlePrice : minPrice;
    const normalizedMaxPrice = isFixedPrice ? singlePrice : maxPrice;

    setSubCategorySaving(true);

    try {
      const payload = {
        subCategory: subCategoryForm.subCategory.trim(),
        userId: Number(user.id),
        categoryId: Number(targetCategory.id),
        fixedPrice: isFixedPrice,
        price: normalizedMinPrice,
        minPrice: normalizedMinPrice,
        maxPrice: normalizedMaxPrice,
      };

      const savedSubCategoryResponse = editingSubCategoryId
        ? await updateSubCategory(editingSubCategoryId, payload)
        : await createSubCategory(payload);
      const savedSubCategory = {
        ...savedSubCategoryResponse,
        categoryId: savedSubCategoryResponse?.categoryId || targetCategory.id,
      };

      setCategories((current) =>
        current.map((category) => {
          const existingItems = (category.subCategories || []).filter(
            (item) => String(item.id) !== String(editingSubCategoryId)
          );

          return {
            ...category,
            subCategories:
              String(category.id) === String(targetCategory.id)
                ? [...existingItems, savedSubCategory]
                : existingItems,
          };
        })
      );

      setSelectedCategoryId(String(targetCategory.id));

      handleSubCategoryCancel();
      return true;
    } catch (err) {
      window.alert(err.message || "Failed to save subcategory");
      return false;
    } finally {
      setSubCategorySaving(false);
    }
  };

  const handleSubCategoryEdit = (item) => {
    const parentCategory = findParentCategoryForSubCategory(item) || selectedCategory;

    setEditingSubCategoryId(item.id);
    setSelectedCategoryId(String(parentCategory?.id || ""));
    const itemMinPrice = item.minPrice || item.price || "";
    const itemMaxPrice = item.maxPrice || item.price || "";
    const isFixedPrice =
      typeof item.fixedPrice === "boolean"
        ? item.fixedPrice
        : !itemMinPrice || !itemMaxPrice || Number(itemMinPrice) === Number(itemMaxPrice);

    setSubCategoryForm({
      category: parentCategory ? getCategoryName(parentCategory) : "",
      subCategory: item.subCategory || "",
      fixedPrice: isFixedPrice,
      price: isFixedPrice ? itemMinPrice : "",
      minPrice: itemMinPrice,
      maxPrice: itemMaxPrice,
    });
  };

  const handleSubCategoryDelete = async (item) => {
    if (!requireAdminSession("subcategories")) {
      return;
    }

    const itemName = item.subCategory || "this subcategory";
    const shouldDelete = window.confirm(`Delete ${itemName}?`);

    if (!shouldDelete) {
      return;
    }

    try {
      await deleteSubCategory(item.id);
      setCategories((current) =>
        current.map((category) => ({
          ...category,
          subCategories: (category.subCategories || []).filter(
            (subCategory) => String(subCategory.id) !== String(item.id)
          ),
        }))
      );

      if (String(editingSubCategoryId) === String(item.id)) {
        handleSubCategoryCancel();
      }
    } catch (err) {
      window.alert(err.message || "Failed to delete subcategory");
    }
  };

  const handleCitySubmit = async () => {
    if (!requireAdminSession("cities")) {
      return false;
    }

    if (!cityForm.name.trim()) {
      window.alert("Please enter city name");
      return false;
    }

    try {
      const payload = { name: cityForm.name.trim() };

      if (editingCityId) {
        const updatedCity = await updateCity(editingCityId, payload);
        setCities((current) =>
          current.map((cityItem) =>
            String(cityItem.id) === String(editingCityId) ? updatedCity : cityItem
          )
        );
      } else {
        const newCity = await createCity(payload);
        setCities((current) => [...current, newCity]);
        setSelectedState((current) => current || newCity.name || "");
      }

      handleCityCancel();
      return true;
    } catch (err) {
      window.alert(err.message || "Failed to save city");
      return false;
    }
  };

  const handleCityEdit = (cityItem) => {
    setEditingCityId(cityItem.id);
    setCityForm({ name: cityItem.name || "" });
  };

  const handleCityDelete = async (cityItem) => {
    if (!requireAdminSession("cities")) {
      return;
    }

    const cityName = cityItem.name || "this city";
    const shouldDelete = window.confirm(`Delete ${cityName}?`);

    if (!shouldDelete) {
      return;
    }

    try {
      await deleteCity(cityItem.id);
      setCities((current) => current.filter((city) => city.id !== cityItem.id));

      if (selectedState === cityItem.name) {
        const nextCity = cities.find((city) => city.id !== cityItem.id);
        setSelectedState(nextCity?.name || "");
      }

      if (editingCityId === cityItem.id) {
        handleCityCancel();
      }
    } catch (err) {
      window.alert(err.message || "Failed to delete city");
    }
  };

  const handlePincodeSubmit = async () => {
    if (!requireAdminSession("pincodes")) {
      return false;
    }

    const normalizedPinCode = String(pincodeForm.pinCode || "").trim();

    if (!normalizedPinCode) {
      window.alert("Please enter pincode");
      return false;
    }

    if (!/^[0-9]{6}$/.test(normalizedPinCode)) {
      window.alert("Please enter a valid 6 digit pincode");
      return false;
    }

    setPincodeSaving(true);

    try {
      const payload = {
        pinCode: normalizedPinCode,
        available: Boolean(pincodeForm.available),
      };

      if (editingPincodeId) {
        const updatedPincode = await updatePincode(editingPincodeId, payload);
        setPincodes((current) =>
          current.map((item) =>
            String(item.id) === String(editingPincodeId) ? updatedPincode : item
          )
        );
      } else {
        const newPincode = await createPincode(payload);
        setPincodes((current) => [...current, newPincode]);
      }

      handlePincodeCancel();
      return true;
    } catch (err) {
      window.alert(err.message || "Failed to save pincode");
      return false;
    } finally {
      setPincodeSaving(false);
    }
  };

  const handlePincodeEdit = (pincodeItem) => {
    setEditingPincodeId(pincodeItem.id);
    setPincodeForm({
      pinCode: pincodeItem.pinCode || "",
      available: pincodeItem.available !== false,
    });
  };

  const handlePincodeAvailabilityChange = async (pincodeItem, available) => {
    if (!requireAdminSession("pincodes")) {
      return;
    }

    if (!pincodeItem?.id) {
      window.alert("Pincode ID is missing");
      return;
    }

    setPincodeSaving(true);

    try {
      const updatedPincode = await updatePincode(pincodeItem.id, {
        pinCode: pincodeItem.pinCode,
        available,
      });

      setPincodes((current) =>
        current.map((item) =>
          String(item.id) === String(pincodeItem.id) ? updatedPincode : item
        )
      );
    } catch (err) {
      window.alert(err.message || "Failed to update pincode");
    } finally {
      setPincodeSaving(false);
    }
  };

  const handlePincodeDelete = async (pincodeItem) => {
    if (!requireAdminSession("pincodes")) {
      return;
    }

    const pinCode = pincodeItem.pinCode || "this pincode";
    const shouldDelete = window.confirm(`Delete ${pinCode}?`);

    if (!shouldDelete) {
      return;
    }

    try {
      await deletePincode(pincodeItem.id);
      setPincodes((current) => current.filter((item) => item.id !== pincodeItem.id));

      if (editingPincodeId === pincodeItem.id) {
        handlePincodeCancel();
      }
    } catch (err) {
      window.alert(err.message || "Failed to delete pincode");
    }
  };

  const handleAcceptOrder = async (orderId) => {
    if (!requireAdminSession("orders")) {
      return;
    }

    try {
      const response = await adminOrderService.acceptOrder(orderId, Number(user.id));
      replaceOrder(response.data);
      return response.data;
    } catch (err) {
      window.alert(err.message || "Failed to accept order");
      throw err;
    }
  };

  const handleAssignOrder = async (orderId, adminId) => {
    if (!requireAdminSession("orders")) {
      return;
    }

    if (!adminId) {
      window.alert("Please select an admin");
      return;
    }

    try {
      const response = await adminOrderService.assignOrder(orderId, Number(adminId));
      replaceOrder(response.data);
      return response.data;
    } catch (err) {
      window.alert(err.message || "Failed to assign order");
      throw err;
    }
  };

  const handleUnassignOrder = async (orderId) => {
    if (!requireAdminSession("orders")) {
      return;
    }

    try {
      const response = await adminOrderService.unassignOrder(orderId, {
        orderId,
        unassigningdUserId: Number(user.id),
      });
      replaceOrder(response.data);
      return response.data;
    } catch (err) {
      window.alert(err.message || "Failed to remove assignment");
      throw err;
    }
  };

  const handleRescheduleOrder = async (orderId, schedule) => {
    if (!requireAdminSession("orders")) {
      return;
    }

    if (!schedule.pickupDate || !schedule.startRange || !schedule.endRange) {
      window.alert("Please enter pickup date and time range");
      return;
    }

    if (schedule.endRange <= schedule.startRange) {
      window.alert("Pickup end time must be after start time");
      return;
    }

    try {
      const response = await adminOrderService.rescheduleOrder(orderId, {
        pickupDate: schedule.pickupDate,
        startRange: schedule.startRange,
        endRange: schedule.endRange,
      });
      replaceOrder(response.data);
    } catch (err) {
      window.alert(err.message || "Failed to reschedule order");
    }
  };

  const handleSendDeliveryOtp = async (orderId) => {
    if (!requireAdminSession("orders")) {
      return;
    }

    try {
      await adminOrderService.sendDeliveryOtp(orderId, Number(user.id));
      setOrders((current) =>
        current.map((order) =>
          String(order.id) === String(orderId)
            ? { ...order, status: "Delivery OTP Sent" }
            : order
        )
      );
      window.alert("Delivery OTP sent to pickup phone and email");
    } catch (err) {
      window.alert(err.message || "Failed to send delivery OTP");
    }
  };

  const handleDeliverOrder = async (orderId, payload) => {
    if (!requireAdminSession("orders")) {
      return;
    }

    const otp = payload?.otp || "";
    const amount = Number(payload?.amount);
    const weight = Number(payload?.weight);

    if (!otp || !otp.trim()) {
      window.alert("Please enter delivery OTP");
      return;
    }

    if (!Number.isFinite(weight) || weight <= 0) {
      window.alert("Please enter final pickup weight");
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      window.alert("Please enter final order amount");
      return;
    }

    try {
      const response = await adminOrderService.deliverOrder(orderId, {
        otp: otp.trim(),
        amount,
        weight,
      });
      replaceOrder(response.data);
    } catch (err) {
      window.alert(err.message || "Failed to deliver order");
    }
  };

  const categoriesProps = {
    categories,
    selectedCategory,
    selectedCategoryId,
    selectedState,
    categoryForm,
    subCategoryForm,
    editingCategoryId,
    editingSubCategoryId,
    onCategoryFormChange: (name, value) =>
      setCategoryForm((current) => ({ ...current, [name]: value })),
    onSubCategoryFormChange: (name, value) => {
      if (name === "category") {
        const nextCategory = findCategoryByFormValue(value);

        if (nextCategory?.id) {
          setSelectedCategoryId(String(nextCategory.id));
        }
      }

      setSubCategoryForm((current) => ({ ...current, [name]: value }));
    },
    onCategorySubmit: handleCategorySubmit,
    onCategoryCancel: handleCategoryCancel,
    onSubCategorySubmit: handleSubCategorySubmit,
    onSubCategoryCancel: handleSubCategoryCancel,
    categorySaving,
    subCategorySaving,
    onSelectCategory: setSelectedCategoryId,
    onCategoryEdit: handleCategoryEdit,
    onCategoryDelete: handleCategoryDelete,
    onSubCategoryEdit: handleSubCategoryEdit,
    onSubCategoryDelete: handleSubCategoryDelete,
  };

  const ordersProps = {
    orders,
    admins,
    addresses,
    categories,
    currentAdminId: Number(user?.id) || 0,
    onAcceptOrder: handleAcceptOrder,
    onAssignOrder: handleAssignOrder,
    onUnassignOrder: handleUnassignOrder,
    onRescheduleOrder: handleRescheduleOrder,
    onSendDeliveryOtp: handleSendDeliveryOtp,
    onDeliverOrder: handleDeliverOrder,
    onRefreshOrders: fetchOrders,
  };

  const citiesProps = {
    cities,
    addresses,
    cityForm,
    editingCityId,
    onCityFormChange: (value) => setCityForm({ name: value }),
    onCitySubmit: handleCitySubmit,
    onCityCancel: handleCityCancel,
    onCityEdit: handleCityEdit,
    onCityDelete: handleCityDelete,
  };

  const pincodeProps = {
    pincodes,
    pincodeForm,
    editingPincodeId,
    pincodeSaving,
    onPincodeFormChange: (name, value) =>
      setPincodeForm((current) => ({ ...current, [name]: value })),
    onPincodeSubmit: handlePincodeSubmit,
    onPincodeCancel: handlePincodeCancel,
    onPincodeEdit: handlePincodeEdit,
    onPincodeAvailabilityChange: handlePincodeAvailabilityChange,
    onPincodeDelete: handlePincodeDelete,
  };

  return {
    activeTab,
    addresses,
    adminInitial,
    adminName,
    admins,
    categories,
    categoriesProps,
    cities,
    citiesProps,
    error,
    loading,
    ordersProps,
    pincodeProps,
    pincodes,
    setActiveTab,
    setShowProfile,
    showProfile,
    user,
    fetchAdmins,
    handleLogout,
  };
};
